import { z } from 'zod';
import defaultConfig from '../config/defaultAppConfig.json';
import { FEATURE_FLAGS, resolveFeatureFlags } from '../config/featureFlags.js';
import { fetchWithTimeout } from './network/fetchWithTimeout.js';
import { buildCandidateUrls } from './runtimePaths.js';
import { logEvent } from '../utils/monitoring.js';

const RUNTIME_CONFIG_PATH = '/app-config.json';
const RUNTIME_CONFIG_CACHE_KEY = '3toolspro_runtime_config_cache';
const CONFIG_STORAGE_KEY = '3toolspro_app_config_override';
const CURRENT_CONFIG_VERSION = 2;

const adPlacementsSchema = z.object({
  'header-banner': z.boolean().default(false),
  'after-hero': z.boolean().default(false),
  'between-sections': z.boolean().default(false),
  sidebar: z.boolean().default(false),
  'tool-bottom': z.boolean().default(false),
  'article-inline': z.boolean().default(false),
  footer: z.boolean().default(false),
}).passthrough();

const adPagePlacementsSchema = z.object({
  home: z.object({
    'after-hero': z.boolean().default(false),
    'after-tools-grid': z.boolean().default(false),
    'footer-inline': z.boolean().default(false),
  }).default({}),
  tool: z.object({
    'after-title': z.boolean().default(false),
    'after-first-content': z.boolean().default(false),
    'before-related-tools': z.boolean().default(false),
    'end-of-page': z.boolean().default(false),
  }).default({}),
}).default({});

const adSlotIdsSchema = z.record(z.string(), z.string()).default({});

const featureFlagsSchema = z.object(
  Object.keys(FEATURE_FLAGS).reduce((acc, key) => {
    acc[key] = z.boolean().default(FEATURE_FLAGS[key]);
    return acc;
  }, {})
).strict();

const configSchema = z.object({
  configVersion: z.number().int().min(1).default(CURRENT_CONFIG_VERSION),
  featureFlags: featureFlagsSchema.default(FEATURE_FLAGS),
  ads: z.object({
    enabled: z.boolean().default(false),
    showAllAds: z.boolean().default(false),
    placements: adPlacementsSchema.default({}),
    pagePlacements: adPagePlacementsSchema,
    slotIds: adSlotIdsSchema,
  }).default({}),
  tools: z.object({
    visibility: z.object({
      enabledTools: z.record(z.string(), z.boolean()).default({}),
      hiddenTools: z.array(z.string()).default([]),
    }).default({}),
    ordering: z.object({
      mode: z.enum(['popularity', 'alphabetical', 'custom', 'usage-popularity']).default('popularity'),
      customOrder: z.array(z.string()).default([]),
    }).default({}),
  }).default({}),
  languages: z.object({
    default: z.string().default('en'),
    supported: z.array(z.string()).default(['en']),
  }).default({}),
  ai: z.object({
    textProvider: z.string().default('gemini'),
    chatProvider: z.string().default('gemini'),
    imageProvider: z.string().default('gemini'),
    maxPerMinute: z.number().optional(),
    maxPerDay: z.number().optional(),
    requestTimeoutMs: z.number().optional(),
  }).default({}),
}).passthrough();

const normalizeToolConfig = (normalized = {}) => ({
  visibility: {
    enabledTools: normalized?.tools?.visibility?.enabledTools || normalized?.enabledTools || {},
    hiddenTools: normalized?.tools?.visibility?.hiddenTools || normalized?.hiddenTools || [],
  },
  ordering: {
    mode: normalized?.tools?.ordering?.mode || normalized?.toolOrdering?.mode || 'popularity',
    customOrder: normalized?.tools?.ordering?.customOrder || normalized?.toolOrdering?.customOrder || [],
  },
});

const normalizeLanguageConfig = (normalized = {}) => ({
  default: normalized?.languages?.default || normalized?.defaultLanguage || 'en',
  supported: normalized?.languages?.supported || defaultConfig?.languages?.supported || ['en'],
});

const normalizeAiLimits = (normalized = {}) => ({
  maxPerMinute: normalized?.ai?.maxPerMinute || normalized?.usageLimits?.aiPerMinute,
  maxPerDay: normalized?.ai?.maxPerDay || normalized?.usageLimits?.aiPerDay,
  requestTimeoutMs: normalized?.ai?.requestTimeoutMs || normalized?.timeouts?.requestMs,
});

const normalizeLegacyConfig = (config) => {
  const normalized = {
    ...config,
    configVersion: Number(config?.configVersion) > 0 ? Number(config.configVersion) : CURRENT_CONFIG_VERSION,
    featureFlags: resolveFeatureFlags(config?.featureFlags),
    tools: normalizeToolConfig(config),
    languages: normalizeLanguageConfig(config),
    ai: {
      ...(config?.ai || {}),
      ...normalizeAiLimits(config),
    },
  };

  const legacyMode = normalized?.tools?.ordering?.mode || normalized?.toolOrdering?.mode;
  if (legacyMode === 'usage-popularity') {
    normalized.tools = {
      ...normalized.tools,
      ordering: {
        ...normalized.tools.ordering,
        mode: 'popularity',
      },
    };
  }

  return normalized;
};

const deepMerge = (base, override) => {
  if (!override || typeof override !== 'object') return base;
  if (!base || typeof base !== 'object') return override;

  const output = { ...base };
  Object.keys(override).forEach((key) => {
    const overrideValue = override[key];
    if (Array.isArray(overrideValue)) {
      output[key] = [...overrideValue];
      return;
    }

    if (overrideValue && typeof overrideValue === 'object') {
      output[key] = deepMerge(base[key], overrideValue);
      return;
    }

    output[key] = overrideValue;
  });
  return output;
};

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const buildConfigCandidates = () => buildCandidateUrls(RUNTIME_CONFIG_PATH);

const loadRuntimeConfig = async () => {
  let runtimeConfig = {};
  const candidates = buildConfigCandidates();

  for (const path of candidates) {
    try {
      const response = await fetchWithTimeout(`${path}?t=${Date.now()}`, {
        cache: 'no-store',
        timeoutMs: 8000,
      });
      if (!response.ok) {
        continue;
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        continue;
      }

      runtimeConfig = await response.json();
      if (canUseStorage()) {
        localStorage.setItem(RUNTIME_CONFIG_CACHE_KEY, JSON.stringify({ config: runtimeConfig, timestamp: Date.now() }));
      }
      return runtimeConfig;
    } catch (error) {
      logEvent('error', 'runtime_config_fetch_failed', { error: error?.message || String(error) });
    }
  }

  if (canUseStorage()) {
    const cached = localStorage.getItem(RUNTIME_CONFIG_CACHE_KEY);
    if (cached) {
      try {
        return JSON.parse(cached)?.config || {};
      } catch {
        // ignore cache parse error
      }
    }
  }

  return runtimeConfig;
};

class ConfigManager {
  constructor() {
    this.config = normalizeLegacyConfig(defaultConfig);
    this.loaded = false;
  }

  async loadConfigOnce() {
    if (this.loaded) return this.config;

    const runtimeConfig = await loadRuntimeConfig();

    let localOverride = {};
    if (canUseStorage()) {
      try {
        localOverride = JSON.parse(localStorage.getItem(CONFIG_STORAGE_KEY) || '{}');
      } catch {
        localOverride = {};
      }
    }

    const candidate = normalizeLegacyConfig(deepMerge(deepMerge(defaultConfig, runtimeConfig), localOverride));
    const parsed = configSchema.safeParse(candidate);
    if (!parsed.success) {
      logEvent('error', 'invalid_runtime_config_fallback', { issues: parsed.error.flatten() });
      const fallbackParsed = configSchema.safeParse(normalizeLegacyConfig(defaultConfig));
      this.config = fallbackParsed.success ? fallbackParsed.data : normalizeLegacyConfig(defaultConfig);
    } else {
      this.config = parsed.data;
    }

    this.loaded = true;
    return this.config;
  }

  setConfig(nextConfig) {
    const candidate = normalizeLegacyConfig(deepMerge(this.config, nextConfig));
    const parsed = configSchema.safeParse(candidate);
    if (!parsed.success) {
      logEvent('error', 'invalid_runtime_override_ignored', { issues: parsed.error.flatten() });
      return this.config;
    }
    this.config = parsed.data;
    return this.config;
  }

  get(path, fallback = undefined) {
    const value = path.split('.').reduce((acc, part) => (acc && Object.prototype.hasOwnProperty.call(acc, part) ? acc[part] : undefined), this.config);
    return value === undefined ? fallback : value;
  }

  isToolEnabled(toolId) {
    if (!toolId) return false;
    const enabledTools = this.get('tools.visibility.enabledTools', {});
    const hiddenTools = this.get('tools.visibility.hiddenTools', []);
    if (hiddenTools.includes(toolId)) return false;
    return enabledTools[toolId] !== false;
  }

  isLanguageSupported(lang) {
    return this.get('languages.supported', []).includes(lang);
  }

  isAdSlotEnabled(slot) {
    return this.get('featureFlags.enableMonetizationAds', false) === true
      && this.get('ads.enabled', false) === true
      && this.get('ads.showAllAds', false) === true
      && this.get(`ads.placements.${slot}`, false) === true;
  }

  isFeatureEnabled(featureFlag) {
    return this.get(`featureFlags.${featureFlag}`, FEATURE_FLAGS[featureFlag] ?? false) === true;
  }
}

const Config = new ConfigManager();

export { Config, configSchema, deepMerge, CONFIG_STORAGE_KEY, RUNTIME_CONFIG_CACHE_KEY };
